/**
 * Raw API config ↔ editor state.
 *
 * Configs from the API: `{ id, file_path, file_data }`. Each maps to:
 *   - `/layout/header*`  → header sections
 *   - `/layout/footer*`  → footer sections
 *   - `/settings/*`      → global theme settings
 *   - `/pages/<id>.json` → page content + meta
 *   - `/locales/<code>.json` → locale strings
 *
 * IDs: templates on disk store no `id` on sections/blocks. Load backfills
 * (`ensureId`); save strips them (`stripIds`). IDs are stable identifiers
 * needed for drag-and-drop, expanded state, etc. — paths can't substitute
 * because they change on reorder.
 */

import isEqual from 'lodash/isEqual';
import { generateId } from './id';

// ---- File path → config type ----

const PATTERNS = {
  header: /\/layout\/header/,
  footer: /\/layout\/footer/,
  settings: /\/settings\//,
  page: /\/pages\/([^/]+)\.json$/,
  locale: /\/locales\/([^/]+?)(?:\.default)?\.json$/,
};

function getConfigType(filePath) {
  if (PATTERNS.header.test(filePath)) return 'header';
  if (PATTERNS.footer.test(filePath)) return 'footer';
  if (PATTERNS.settings.test(filePath)) return 'settings';
  if (PATTERNS.locale.test(filePath)) return 'locale';
  if (PATTERNS.page.test(filePath)) return 'page';
  return null;
}

const pageIdOf = (filePath) => filePath.match(PATTERNS.page)?.[1] ?? null;
const localeOf = (filePath) => filePath.match(PATTERNS.locale)?.[1] ?? null;

function parse(fileData) {
  if (typeof fileData === 'string') {
    try {
      return JSON.parse(fileData);
    } catch {
      return {};
    }
  }
  return typeof fileData === 'object' && fileData !== null ? fileData : {};
}

// ---- Id lifecycle ----

function ensureBlockId(block) {
  return { ...block, id: block.id ?? generateId(block.type) };
}

function ensureBlocks(blocks) {
  return Object.fromEntries(
    Object.entries(blocks).map(([slotId, items]) => [
      slotId,
      items.map(ensureBlockId),
    ]),
  );
}

function ensureId(section) {
  const result = { ...section, id: section.id ?? generateId(section.type) };
  if (section.blocks) result.blocks = ensureBlocks(section.blocks);
  return result;
}

function stripBlockId(block) {
  const { id: _id, ...rest } = block;
  return rest;
}

function stripBlocks(blocks) {
  return Object.fromEntries(
    Object.entries(blocks).map(([slotId, items]) => [
      slotId,
      items.map(stripBlockId),
    ]),
  );
}

function stripSection(section) {
  const { id: _id, blocks, ...rest } = section;
  if (blocks && Object.keys(blocks).length > 0) {
    return { ...rest, blocks: stripBlocks(blocks) };
  }
  return rest;
}

function stripIds(sections) {
  return sections.map(stripSection);
}

// ---- Per-type parsers ----

function parseSections(config) {
  const parsed = parse(config.file_data);
  return (parsed.sections ?? []).map(ensureId);
}

function parseSettings(config) {
  const parsed = parse(config.file_data);
  return parsed.current ?? parsed;
}

function parsePage(config) {
  const parsed = parse(config.file_data);
  return {
    content: (parsed.sections ?? []).map(ensureId),
    pageMeta: parsed.page ?? { title: '', description: '' },
  };
}

// ---- Public: parseConfigs (one-shot at setup) ----

function applyConfig(result, config) {
  const type = getConfigType(config.file_path);

  if (type === 'header') {
    result.header = parseSections(config);
  } else if (type === 'footer') {
    result.footer = parseSections(config);
  } else if (type === 'settings') {
    result.settings = parseSettings(config);
  } else if (type === 'page') {
    const pageId = pageIdOf(config.file_path);
    if (pageId) result.pageContents[pageId] = parsePage(config);
  } else if (type === 'locale') {
    const code = localeOf(config.file_path);
    if (code) result.locales[code] = parse(config.file_data);
  }
}

/**
 * Returns `{ header, footer, settings, locales, pageContents }`.
 * `pageContents` is a map: `{ [pageId]: { content, pageMeta } }`.
 */
export function parseConfigs(rawConfigs) {
  const result = {
    header: [],
    footer: [],
    settings: {},
    locales: {},
    pageContents: {},
  };

  for (const config of rawConfigs ?? []) {
    applyConfig(result, config);
  }

  return result;
}

// ---- Public: find a config record by group/page ----

function configMatchesGroup(config, group, pageId) {
  const type = getConfigType(config.file_path);
  if (group === 'content') {
    return type === 'page' && pageIdOf(config.file_path) === pageId;
  }
  return type === group;
}

export function findConfig(configs, group, pageId) {
  return (
    (configs ?? []).find((config) =>
      configMatchesGroup(config, group, pageId),
    ) ?? null
  );
}

export function findLocaleConfig(configs, code) {
  return (
    (configs ?? []).find(
      (config) =>
        getConfigType(config.file_path) === 'locale' &&
        localeOf(config.file_path) === code,
    ) ?? null
  );
}

/** Build a `{ [code]: localeData }` map from raw locale configs. */
export function localesFromConfigs(configs) {
  const locales = {};
  for (const config of configs ?? []) {
    if (getConfigType(config.file_path) !== 'locale') {
      continue;
    }
    const code = localeOf(config.file_path);
    if (code) {
      locales[code] = parse(config.file_data);
    }
  }
  return locales;
}

// ---- Save: build file_data payload ----

function buildContentFileData(base, { content, pageMeta }) {
  return { ...base, page: pageMeta, sections: stripIds(content) };
}

function buildSettingsFileData(base, settings) {
  return { ...base, current: settings };
}

function buildSectionsFileData(base, sections) {
  return { ...base, sections: stripIds(sections) };
}

function buildFileData(config, group, payload) {
  const base = parse(config.file_data);
  if (group === 'content') return buildContentFileData(base, payload);
  if (group === 'settings') return buildSettingsFileData(base, payload);
  return buildSectionsFileData(base, payload);
}

// ---- Save: dirty detection ----

function buildTaskIfDirty(configs, group, pageId, payload) {
  const config = findConfig(configs, group, pageId);
  if (!config) return null;

  const fileData = buildFileData(config, group, payload);
  if (isEqual(parse(config.file_data), fileData)) return null;

  return { group, pageId, configId: config.id, fileData };
}

function collectGlobalTasks(state) {
  const current = state.data.present;
  return ['header', 'footer', 'settings']
    .map((group) =>
      buildTaskIfDirty(state.configs, group, undefined, current[group]),
    )
    .filter(Boolean);
}

function collectCurrentPageTask(state) {
  if (!state.page?.id) return null;
  const current = state.data.present;
  return buildTaskIfDirty(state.configs, 'content', state.page.id, {
    content: current.content,
    pageMeta: current.pageMeta,
  });
}

function collectOtherPageTasks(state) {
  const tasks = [];
  for (const [pageId, pageData] of Object.entries(state.pageContents ?? {})) {
    if (pageId === state.page?.id) continue;
    const task = buildTaskIfDirty(state.configs, 'content', pageId, pageData);
    if (task) tasks.push(task);
  }
  return tasks;
}

/**
 * Walks current state, returns list of save tasks for every dirty group.
 * Each task: `{ group, pageId?, configId, fileData }`.
 */
export function collectDirtyTasks(state) {
  return [
    ...collectGlobalTasks(state),
    collectCurrentPageTask(state),
    ...collectOtherPageTasks(state),
  ].filter(Boolean);
}
